<template>
  <div class="bimetallic-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$热膨胀量 ΔL = α · L₀ · ΔT$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-bm"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              <!-- Heat wave texture -->
              <linearGradient id="heat-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                 <stop offset="0%" stop-color="rgba(244,63,94,0)" />
                 <stop offset="100%" stop-color="rgba(244,63,94,0.3)" />
              </linearGradient>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Mode: Straight Strip (温控开关) -->
           <g v-if="mode === 'strip'">
              <!-- Backplate/Mount -->
              <rect x="50" y="250" width="30" height="100" fill="#334155" />
              <circle cx="65" cy="300" r="6" fill="#1e293b" />
              
              <g transform="translate(80, 275)">
                 <!-- Metal A - High Expansion (Yellow/Brass) -->
                 <path :d="upperPath" fill="#facc15" stroke="#ca8a04" stroke-width="2" filter="url(#glow-bm)" />
                 <!-- Metal B - Low Expansion (Gray/Invar) -->
                 <path :d="lowerPath" fill="#94a3b8" stroke="#475569" stroke-width="2" />
                 
                 <text x="35" y="-15" fill="#facc15" font-size="20" font-weight="900">金属 A (高膨胀系数 α₁)</text>
                 <text x="35" y="65" fill="#94a3b8" font-size="20" font-weight="900">金属 B (低膨胀系数 α₂)</text>
              </g>

              <!-- Electrical Contact Block -->
              <rect x="550" y="320" width="40" height="60" fill="#334155" />
              <circle cx="570" cy="320" r="12" :fill="isContact ? '#10b981' : '#f43f5e'" filter="url(#glow-bm)" />
              
              <!-- Contact Lines -->
              <line x1="570" y1="320" x2="570" y2="450" stroke="#cbd5e1" stroke-width="4" />
              <line x1="80" y1="350" x2="80" y2="450" stroke="#cbd5e1" stroke-width="4" />
              <path :d="wirePath" fill="none" :stroke="isContact ? '#10b981' : '#475569'" stroke-width="6" stroke-dasharray="10,6" filter="url(#glow-bm)">
                 <animate v-if="isContact" attributeName="stroke-dashoffset" values="16;0" dur="0.5s" repeatCount="indefinite" />
              </path>

              <text x="500" y="290" :fill="isContact ? '#10b981' : '#f43f5e'" font-size="24" font-weight="900">{{ isContact ? '电路闭合工作中' : '过热保护！触点断开' }}</text>
           </g>

           <!-- Mode: Spiral Thermometer (螺旋温度计) -->
           <g v-if="mode === 'thermometer'">
              <g transform="translate(400, 300)">
                 <!-- Dial Background -->
                 <circle cx="0" cy="0" r="180" fill="#0f172a" stroke="#334155" stroke-width="6" />
                 
                 <!-- Dial Marks -->
                 <g v-for="i in 13" :key="'dm'+i" :transform="`rotate(${(i-1)*20 - 120})`">
                    <line x1="0" y1="-150" x2="0" y2="-170" stroke="#94a3b8" stroke-width="3" />
                    <text x="0" y="-120" fill="#cbd5e1" font-size="16" font-weight="900" text-anchor="middle" :transform="`rotate(${120 - (i-1)*20}, 0, -120)`">{{ (i-1)*10 }}</text>
                 </g>

                 <!-- The Bimetallic Spring (Spiral) -->
                 <path :d="spiralPathBase" fill="none" stroke="#94a3b8" stroke-width="8" opacity="0.6"/>
                 <path :d="spiralPath" fill="none" stroke="#facc15" stroke-width="8" />

                 <!-- The Needle -->
                 <g :transform="`rotate(${temp * 2 - 120})`">
                    <polygon points="-5,0 5,0 0,-160" fill="#f43f5e" filter="url(#glow-bm)" />
                    <circle cx="0" cy="0" r="12" fill="#e11d48" stroke="#fff" stroke-width="3" />
                 </g>

                 <text x="0" y="80" fill="#38bdf8" font-size="28" font-weight="900" text-anchor="middle">℃</text>
              </g>
           </g>

           <!-- Global Heat Source Visualizer -->
           <rect x="0" y="600" width="800" :height="Math.max(0, (temp - 20) * 3)" fill="url(#heat-grad)" transform="scale(1, -1) translate(0, -1200)" />
           <g v-if="temp > 50" transform="translate(400, 500)">
              <path d="M -30,0 Q 0,-80 30,0 Z" fill="#f43f5e" filter="url(#glow-bm)" opacity="0.6">
                 <animate attributeName="opacity" values="0.3;0.8;0.3" dur="0.8s" repeatCount="indefinite" />
              </path>
           </g>
           <text v-if="temp > 50" x="400" y="550" fill="#fda4af" font-size="18" font-weight="900" text-anchor="middle">环境受热膨胀加剧</text>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">热膨胀力学引擎分析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前状态计算</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>环境温度 (T)</span><span class="hl" :class="temp > 60 ? 'text-red' : 'text-blue'">{{ temp }} °C</span></div>
                  
                  <template v-if="mode === 'strip'">
                     <div class="data-row"><span>金属 A 伸长量</span><span class="hl text-yellow">+{{ bendTotal.toFixed(2) }} mm</span></div>
                     <div class="data-row"><span>金属 B 伸长量</span><span class="hl text-blue">+{{ (bendTotal*0.3).toFixed(2) }} mm</span></div>
                     <div class="data-row" :style="{ background: isContact ? 'rgba(16,185,129,0.1)' : 'rgba(244,63,94,0.1)' }">
                        <span>末端形变曲率</span><span class="hl" :class="isContact ? 'text-green' : 'text-red'">{{ bendOffset.toFixed(1) }} px</span>
                     </div>
                  </template>

                  <template v-if="mode === 'thermometer'">
                     <div class="data-row"><span>盘簧旋紧应变矩</span><span class="hl text-yellow">{{ (temp * 0.15).toFixed(2) }} N·m</span></div>
                     <div class="data-row" style="background: rgba(56,189,248,0.1)">
                        <span>中央探针偏转角</span><span class="hl text-blue">{{ (temp * 2).toFixed(1) }}°</span>
                     </div>
                  </template>

               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>知识点模式选择 (工程应用形态)</label>
               <el-radio-group v-model="mode" size="small">
                 <el-radio-button label="strip">双金属片 (温控/断路器)</el-radio-button>
                 <el-radio-button label="thermometer">双金属温度计 (螺旋盘簧)</el-radio-button>
               </el-radio-group>
            </div>
            
            <div class="s-item" style="grid-column: span 2;">
               <label>环境加热/冷却温度池控制</label>
               <el-slider v-model="temp" :min="0" :max="120" :step="1" size="small"></el-slider>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BimetallicViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'strip',
      temp: 20
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'thermometer') return '双金属温度计：机械应变放大的精密测量';
       return '双金属片：不同膨胀率引发的非对称弯曲与自动温控';
    },
    // Mechanics properties for Straight Strip
    bendOffset() {
       // temp base 20. Bends upwards if temp < 20, downwards if temp > 20
       return (this.temp - 20) * 1.5; 
    },
    bendTotal() {
       return Math.max(0, this.temp - 20) * 0.2;
    },
    // The switch breaks at Temp > 80, Offset > 90
    isContact() {
       return this.bendOffset <= 85;
    },
    upperPath() {
       let b = this.bendOffset;
       // Bezier curve to simulate physical blending of a cantilever beam
       return `M 0,0 L 450,0 Q 450,${b} 480,${b} Q 500,${b} 500,${b+15} L 0,15 Z`;
    },
    lowerPath() {
       let b = this.bendOffset;
       return `M 0,15 L 500,${b+15} Q 500,${b+30} 480,${b+30} Q 450,${b+30} 450,30 L 0,30 Z`;
    },
    wirePath() {
       return `M 80 450 L 570 450`;
    },
    
    // Mechanics properties for Spiral
    spiralPathBase() {
       return this.calcSpiral(0); 
    },
    spiralPath() {
       let extraTurns = (this.temp - 20) * 0.005; 
       return this.calcSpiral(extraTurns);
    },
    tipDesc() {
       if (this.mode === 'thermometer') return '将双金属片卷成螺旋形（或发条盘簧状）。当温度升高时，内外交错的热膨胀让螺旋弹簧越收越紧（或越松），这个卷曲变形力由于多圈的累积效应被显著放大，直接带动中心的齿轮或探针转动。这种温度计抗震性好，极其耐操，不需电池！';
       return '将两片线膨胀系数截然不同（比如黄铜和殷钢）的金属片紧紧贴合在一起。温度升高时，黄铜想变得很长，而殷钢几乎不想变，导致整体必须向“短”的那边（殷钢侧）发生严重弯曲变形！当弯曲到一定程度时，它便会自动推开并切断电路，电熨斗、电水壶保温就是这个极其简单暴力的物理原理。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/温度计|表/.test(kp)) this.mode = 'thermometer';
      else this.mode = 'strip';
    },
    calcSpiral(extraTurns) {
       let d = "M 0 0 ";
       let turns = 3.5 + extraTurns;
       let points = 100;
       for (let i = 0; i <= points; i++) {
          let angle = (i / points) * Math.PI * 2 * turns;
          let r = (i / points) * 120;
          let x = r * Math.cos(angle);
          let y = r * Math.sin(angle);
          d += `L ${x} ${y} `;
       }
       return d;
    }
  }
}
</script>

<style scoped lang="scss">
.bimetallic-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-yellow { color: #facc15; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
